import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { Seo } from '@/lib/seo';
import { useAuth } from '@/lib/auth';
import { Loader, ArrowRight, Briefcase, Award, Star, Warning, Phone, Mail } from '@/components/ui/Icons';

type Stats = {
  by_status: Record<'new'|'contacted'|'quoted'|'won'|'lost'|'spam', number>;
  total_non_spam: number;
  won_rate: number;
  last_week: { d: string; c: number }[];
};

type Latest = {
  id: number; name: string; email: string; service: string;
  status: keyof Stats['by_status']; created_at: string;
};

type Resp = {
  ok: true;
  user: { email: string; name: string; is_super: boolean; picture: string | null };
  stats: Stats;
  latest: Latest[];
  super?: { signins_24h: number; signin_failures_24h: number };
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const { data, isLoading, isError } = useQuery<Resp>({
    queryKey: ['admin-dashboard'],
    queryFn: () => api<Resp>('/admin/dashboard'),
  });

  const greeting = greetingForTime();
  const firstName = (data?.user.name ?? user?.name ?? '').split(' ')[0] || 'there';

  return (
    <>
      <Seo title="Dashboard" description="Admin dashboard" path="/admin" noindex />

      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-wrap items-end justify-between gap-3"
      >
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-700">
            {greeting}
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold text-navy-900 sm:text-4xl">
            Welcome back, {firstName}.
          </h1>
          <p className="mt-1 text-sm text-ink-700">
            Here&rsquo;s what&rsquo;s come through the website lately.
          </p>
        </div>
        {data?.user.is_super && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-gold-400/60 bg-gold-400/10 px-3 py-1 text-xs font-semibold text-gold-700">
            <Award className="h-3 w-3" /> Super-admin
          </span>
        )}
      </motion.header>

      {isLoading ? (
        <div className="mt-10 flex items-center gap-2 text-ink-700">
          <Loader className="h-5 w-5" /> Loading dashboard…
        </div>
      ) : isError || !data ? (
        <div className="mt-10 rounded-md border border-red-300 bg-red-50 p-4 text-red-700">
          Could not load the dashboard. Please refresh.
        </div>
      ) : (
        <>
          {/* Stat tiles */}
          <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat title="New" value={data.stats.by_status.new} accent="gold" hint="Awaiting first reply" />
            <Stat title="In progress" value={data.stats.by_status.contacted + data.stats.by_status.quoted} accent="navy" hint="Contacted or quoted" />
            <Stat title="Won" value={data.stats.by_status.won} accent="green" hint="Booked or completed" />
            <Stat title="Win rate" value={`${data.stats.won_rate}%`} accent="navy" hint="Excludes spam" />
          </section>

          {data.user.is_super && data.super && (
            <section className="mt-4 grid gap-4 sm:grid-cols-2">
              <Stat title="Sign-ins (24h)" value={data.super.signins_24h} accent="navy" hint="All admin sign-in attempts" />
              <Stat title="Failures (24h)" value={data.super.signin_failures_24h} accent={data.super.signin_failures_24h > 0 ? 'red' : 'green'} hint="Blocked / wrong account" />
            </section>
          )}

          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {/* Recent activity */}
            <section className="lg:col-span-2 rounded-2xl border border-paper-100 bg-paper-0 p-6 shadow-card">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-navy-900">Latest enquiries</h2>
                <Link to="/admin/quotes" className="inline-flex items-center gap-1 text-sm font-semibold text-gold-700 hover:text-gold-600">
                  View all <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              {data.latest.length === 0 ? (
                <p className="rounded-md border border-paper-100 bg-paper-50 p-4 text-sm text-ink-700">
                  Nothing new at the moment. We&rsquo;ll let you know.
                </p>
              ) : (
                <ul className="divide-y divide-paper-100">
                  {data.latest.map((q) => (
                    <li key={q.id}>
                      <Link
                        to={`/admin/quotes/${q.id}`}
                        className="flex items-start justify-between gap-3 py-3 transition hover:bg-paper-50"
                      >
                        <div className="min-w-0">
                          <p className="font-semibold text-navy-900 truncate">{q.name}</p>
                          <p className="text-xs text-ink-700 truncate">{q.email} · {q.service}</p>
                        </div>
                        <div className="shrink-0 text-right">
                          <StatusPill status={q.status} />
                          <p className="mt-1 text-xs text-ink-700">{relativeTime(q.created_at)}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Quick actions / contact card */}
            <section className="rounded-2xl border border-paper-100 bg-paper-0 p-6 shadow-card">
              <h2 className="font-display text-xl font-semibold text-navy-900">Quick actions</h2>
              <ul className="mt-3 space-y-2 text-sm">
                <li>
                  <Link to="/admin/quotes" className="flex items-center justify-between rounded-md border border-paper-100 bg-paper-50 px-3 py-3 hover:border-gold-300">
                    <span className="inline-flex items-center gap-2 font-semibold text-navy-800">
                      <Briefcase className="h-4 w-4 text-gold-600" /> Quote inbox
                    </span>
                    <ArrowRight className="h-3 w-3 text-ink-700" />
                  </Link>
                </li>
                <li>
                  <Link to="/admin/quotes?status=new" className="flex items-center justify-between rounded-md border border-paper-100 bg-paper-50 px-3 py-3 hover:border-gold-300">
                    <span className="inline-flex items-center gap-2 font-semibold text-navy-800">
                      <Star className="h-4 w-4 text-gold-600" /> Untouched (new)
                    </span>
                    <ArrowRight className="h-3 w-3 text-ink-700" />
                  </Link>
                </li>
                <li>
                  <Link to="/admin/quotes?status=spam" className="flex items-center justify-between rounded-md border border-paper-100 bg-paper-50 px-3 py-3 hover:border-gold-300">
                    <span className="inline-flex items-center gap-2 font-semibold text-navy-800">
                      <Warning className="h-4 w-4 text-gold-600" /> Spam ({data.stats.by_status.spam})
                    </span>
                    <ArrowRight className="h-3 w-3 text-ink-700" />
                  </Link>
                </li>
                {data.user.is_super && (
                  <>
                    <li>
                      <Link to="/admin/super/events" className="flex items-center justify-between rounded-md border border-gold-400/40 bg-gold-400/5 px-3 py-3 hover:border-gold-500/60">
                        <span className="inline-flex items-center gap-2 font-semibold text-navy-800">
                          <Award className="h-4 w-4 text-gold-700" /> Activity feed
                        </span>
                        <ArrowRight className="h-3 w-3 text-ink-700" />
                      </Link>
                    </li>
                    <li>
                      <Link to="/admin/super/login-attempts" className="flex items-center justify-between rounded-md border border-gold-400/40 bg-gold-400/5 px-3 py-3 hover:border-gold-500/60">
                        <span className="inline-flex items-center gap-2 font-semibold text-navy-800">
                          <Award className="h-4 w-4 text-gold-700" /> Login attempts
                        </span>
                        <ArrowRight className="h-3 w-3 text-ink-700" />
                      </Link>
                    </li>
                    <li>
                      <Link to="/admin/super/audit" className="flex items-center justify-between rounded-md border border-gold-400/40 bg-gold-400/5 px-3 py-3 hover:border-gold-500/60">
                        <span className="inline-flex items-center gap-2 font-semibold text-navy-800">
                          <Award className="h-4 w-4 text-gold-700" /> Audit log
                        </span>
                        <ArrowRight className="h-3 w-3 text-ink-700" />
                      </Link>
                    </li>
                  </>
                )}
              </ul>

              <h3 className="mt-6 text-xs font-semibold uppercase tracking-wider text-ink-700/70">Reach the team</h3>
              <div className="mt-2 space-y-1 text-sm">
                <a href="tel:08002888007" className="inline-flex items-center gap-2 text-navy-800 hover:text-gold-700"><Phone className="h-3.5 w-3.5" /> 0800 288 8007</a>
                <br />
                <a href="mailto:BlissbuildingcontractorsLtd@gmail.com" className="inline-flex items-center gap-2 text-navy-800 hover:text-gold-700"><Mail className="h-3.5 w-3.5" /> Bliss inbox</a>
              </div>
            </section>
          </div>
        </>
      )}
    </>
  );
}

function Stat({
  title,
  value,
  hint,
  accent,
}: {
  title: string;
  value: number | string;
  hint?: string;
  accent: 'gold' | 'navy' | 'green' | 'red';
}) {
  const colours: Record<string, string> = {
    gold: 'from-gold-400/15 to-gold-300/0 ring-gold-300/40 text-gold-700',
    navy: 'from-navy-200/30 to-navy-100/0 ring-navy-200/60 text-navy-800',
    green: 'from-green-200/30 to-green-100/0 ring-green-200/60 text-green-800',
    red: 'from-red-200/30 to-red-100/0 ring-red-200/60 text-red-800',
  };
  return (
    <div className={`rounded-2xl border border-paper-100 bg-linear-to-br ${colours[accent]} bg-paper-0 p-5 shadow-card ring-1`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-ink-700/70">{title}</p>
      <p className="mt-2 font-display text-3xl font-semibold text-navy-900">{value}</p>
      {hint && <p className="mt-1 text-xs text-ink-700">{hint}</p>}
    </div>
  );
}

function StatusPill({ status }: { status: keyof Stats['by_status'] }) {
  const map: Record<string, string> = {
    new: 'bg-gold-100 text-gold-700',
    contacted: 'bg-blue-100 text-blue-800',
    quoted: 'bg-purple-100 text-purple-800',
    won: 'bg-green-100 text-green-800',
    lost: 'bg-red-100 text-red-800',
    spam: 'bg-paper-100 text-ink-700',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${map[status]}`}>
      {status}
    </span>
  );
}

function greetingForTime(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}
